import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { api } from 'common';
import {
  PARTNER_TYPES, accentColorForType,
} from 'common/src/other/PartnerUtils';
import {
  Box, Typography, Stack, TextField, InputAdornment, Chip
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import HotelIcon from '@mui/icons-material/Hotel';
import BedroomChildIcon from '@mui/icons-material/BedroomChild';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import LocalPharmacyIcon from '@mui/icons-material/LocalPharmacy';
import StorefrontIcon from '@mui/icons-material/Storefront';
import PartnerCard from '../components/ui/PartnerCard';
import CircularLoading from '../components/CircularLoading';
import { FONT_FAMILY, MAIN_COLOR } from '../common/sharedFunctions';

const ICONS = {
  HOTEL: <HotelIcon fontSize="small" />,
  MOTEL: <BedroomChildIcon fontSize="small" />,
  SUPERMARKET: <ShoppingCartIcon fontSize="small" />,
  PHARMACY: <LocalPharmacyIcon fontSize="small" />,
  STORE: <StorefrontIcon fontSize="small" />,
};

export default function BrowsePartners() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const partnerdata = useSelector((s) => s.partnerdata);
  const [filter, setFilter] = useState('ALL');
  const [search, setSearch] = useState('');
  const { fetchPartners } = api;

  useEffect(() => {
    dispatch(fetchPartners());
  }, [dispatch, fetchPartners]);

  const visible = useMemo(() => {
    const list = partnerdata.partners || [];
    return list
      .filter((p) => p.approved && p.active !== false)
      .filter((p) => filter === 'ALL' || p.partnerType === filter)
      .filter((p) => {
        if (!search) return true;
        const q = search.toLowerCase();
        return (
          (p.businessName || '').toLowerCase().includes(q) ||
          (p.address?.city || '').toLowerCase().includes(q)
        );
      });
  }, [partnerdata.partners, filter, search]);

  return (
    <Box sx={{ p: { xs: 1.5, md: 3 }, fontFamily: FONT_FAMILY }}>
      <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
        {t('marketplace_title') || 'Reserva tu próxima experiencia'}
      </Typography>
      <Typography color="text.secondary" sx={{ mb: 3 }}>
        {t('marketplace_subtitle') || 'Hoteles, moteles, mercados, farmacias y tiendas con un solo toque.'}
      </Typography>

      <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} alignItems="center" sx={{ mb: 3 }}>
        <TextField
          fullWidth
          size="medium"
          placeholder={t('search_placeholder') || 'Buscar por ciudad o nombre...'}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
            sx: { borderRadius: 999, backgroundColor: '#FAFAFA', height: 52 }
          }}
        />
      </Stack>

      <Stack direction="row" spacing={1} sx={{ overflowX: 'auto', pb: 1.5, mb: 2 }}>
        <Chip
          label={t('all') || 'Todos'}
          variant={filter === 'ALL' ? 'filled' : 'outlined'}
          onClick={() => setFilter('ALL')}
          sx={filter === 'ALL' ? { backgroundColor: MAIN_COLOR, color: '#fff', fontWeight: 600 } : { fontWeight: 600 }}
        />
        {Object.values(PARTNER_TYPES).map((pt) => {
          const active = filter === pt;
          const accent = accentColorForType(pt);
          return (
            <Chip
              key={pt}
              icon={ICONS[pt]}
              label={t(`partner_category_${pt.toLowerCase()}`) || pt}
              variant={active ? 'filled' : 'outlined'}
              onClick={() => setFilter(pt)}
              sx={active ? { backgroundColor: accent, color: '#fff', fontWeight: 600 } : { fontWeight: 600 }}
            />
          );
        })}
      </Stack>

      {partnerdata.loading && !partnerdata.partners ? (
        <CircularLoading />
      ) : (
        <Box sx={{
          display: 'grid',
          gap: 2.5,
          gridTemplateColumns: {
            xs: '1fr',
            sm: 'repeat(2, 1fr)',
            md: 'repeat(3, 1fr)',
            lg: 'repeat(4, 1fr)',
          },
        }}>
          {visible.map((p) => (
            <PartnerCard
              key={p.id}
              partner={p}
              onClick={() => navigate(`/partners/${p.id}`)}
            />
          ))}
          {visible.length === 0 && (
            <Box sx={{ gridColumn: '1 / -1', textAlign: 'center', py: 6 }}>
              <Typography color="text.secondary">
                {t('no_results') || 'Sin resultados'}
              </Typography>
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
}
